// Modal de configuração para iniciar uma nova SIMULAÇÃO DE PROVA.
// Aberto sobre o ProvaWorkspace quando o aluno entra sem prova ativa.
//
// 3 escolhas principais:
//   1) Escopo  — Todas / Filtrar por tema-fonte-dificuldade / Números
//   2) Tempo   — horas + minutos (0 = sem limite, modo livre)
//   3) Exibição — regressivo, mostrar relógios, ocultar tema/dificuldade
//
// Confirmar dispara `iniciarProvaConfigurada(config)` no ProvaWorkspace
// que cria o provaState do zero, grava no servidor (autosave) e renderiza
// a primeira questão.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  X,
  Flag,
  Clock,
  Eye,
  Filter as FilterIcon,
  AlertCircle
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import type { ProvaConfig, QuestaoStudy } from "./api";
import { useModalGrowShrink } from "./modalOrigin";
import { iniciarProvaConfigurada, parseNumerosRaw } from "./ProvaWorkspace";

interface State {
  bankId: string;
  questoes: QuestaoStudy[];
}

const open = signal<State | null>(null);

export function openProvaStartModal(bankId: string, questoes: QuestaoStudy[]) {
  open.value = { bankId, questoes };
}

export function closeProvaStartModal() {
  open.value = null;
}

// Esc fecha
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && open.value) {
      open.value = null;
    }
  });
}

export function ProvaStartModal() {
  const state = open.value;
  const isOpen = !!state;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => closeProvaStartModal()
  );
  if (!state) return null;
  return (
    <div
      class="fixed inset-0 z-[60] grid min-h-full place-items-center overflow-hidden bg-black/60 p-4 backdrop-blur-sm"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={ref}
        class="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <ProvaStartContent state={state} requestClose={requestClose} />
      </div>
    </div>
  );
}

type EscopoTipo = "todas" | "personalizar" | "numeros";

function ProvaStartContent({
  state,
  requestClose
}: {
  state: State;
  requestClose: () => void;
}) {
  const { questoes } = state;

  // Catálogos disponíveis no banco (pra montar os pickers de filtro).
  const cat = useMemo(() => {
    const temas = new Map<string, number>();
    const fontes = new Map<string, number>();
    const dificuldades = new Set<number>();
    for (const q of questoes) {
      if (q.tema) temas.set(q.tema, (temas.get(q.tema) ?? 0) + 1);
      if (q.fonte) fontes.set(q.fonte, (fontes.get(q.fonte) ?? 0) + 1);
      if (q.dificuldade) dificuldades.add(Number(q.dificuldade));
    }
    return {
      temas: Array.from(temas.entries()).sort((a, b) => a[0].localeCompare(b[0])),
      fontes: Array.from(fontes.entries()).sort((a, b) => a[0].localeCompare(b[0])),
      dificuldades: Array.from(dificuldades).sort((a, b) => a - b)
    };
  }, [questoes]);

  const [escopoTipo, setEscopoTipo] = useState<EscopoTipo>("todas");
  const [temasSel, setTemasSel] = useState<Set<string>>(new Set());
  const [fontesSel, setFontesSel] = useState<Set<string>>(new Set());
  const [dificuldadesSel, setDificuldadesSel] = useState<Set<number>>(new Set());
  const [numerosRaw, setNumerosRaw] = useState("");

  const [horas, setHoras] = useState(0);
  const [minutos, setMinutos] = useState(60);
  const [regressivo, setRegressivo] = useState(true);
  const [mostrarRelogioGeral, setMostrarRelogioGeral] = useState(true);
  const [mostrarRelogioQuestao, setMostrarRelogioQuestao] = useState(false);
  const [mostrarRelogioRitmo, setMostrarRelogioRitmo] = useState(true);
  const [ocultarTema, setOcultarTema] = useState(true);
  const [ocultarDificuldade, setOcultarDificuldade] = useState(true);

  // Contagem prévia do escopo escolhido.
  const escopoCount = useMemo(() => {
    if (escopoTipo === "todas") return questoes.length;
    if (escopoTipo === "personalizar") {
      return questoes.filter((q) => {
        if (temasSel.size > 0 && (!q.tema || !temasSel.has(q.tema))) return false;
        if (fontesSel.size > 0 && (!q.fonte || !fontesSel.has(q.fonte))) return false;
        if (dificuldadesSel.size > 0 && dificuldadesSel.size < cat.dificuldades.length) {
          const d = Number(q.dificuldade ?? 0);
          if (!dificuldadesSel.has(d)) return false;
        }
        return true;
      }).length;
    }
    if (escopoTipo === "numeros") {
      return parseNumerosRaw(numerosRaw, questoes.length).size;
    }
    return 0;
  }, [escopoTipo, temasSel, fontesSel, dificuldadesSel, numerosRaw, questoes, cat.dificuldades.length]);

  const tempoAlvoMs = (horas * 3600 + minutos * 60) * 1000;

  const podeIniciar = escopoCount > 0;

  function handleConfirm() {
    if (!podeIniciar) return;
    const config: ProvaConfig = {
      tempoAlvoMs,
      regressivo,
      mostrarRelogioGeral,
      mostrarRelogioQuestao,
      mostrarRelogioRitmo,
      ocultarTema,
      ocultarDificuldade,
      escopo:
        escopoTipo === "todas"
          ? { tipo: "todas" }
          : escopoTipo === "personalizar"
            ? {
                tipo: "personalizar",
                temas: Array.from(temasSel),
                fontes: Array.from(fontesSel),
                dificuldade: Array.from(dificuldadesSel)
              }
            : { tipo: "numeros", numerosRaw }
    };
    iniciarProvaConfigurada(config);
    closeProvaStartModal();
  }

  return (
    <>
      <header class="flex shrink-0 items-center justify-between gap-3 border-b border-border px-6 py-4">
        <div class="flex items-center gap-2.5">
          <Flag width="1.3em" height="1.3em" className="text-primary" />
          <h2 class="text-base font-bold">Iniciar nova simulação</h2>
        </div>
        <button
          type="button"
          onClick={requestClose}
          aria-label="Fechar"
          class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <div class="scroll-thin flex-1 space-y-5 overflow-y-auto px-6 py-5">
        {/* Aviso 2026-05-23: aluno chegava esperando ver o progresso do Modo
            Estudo retomado aqui. Esclarecer que simulação começa do zero. */}
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <div class="mb-1 flex items-center gap-2 font-bold text-amber-700 dark:text-amber-300">
            <AlertCircle className="size-4" />
            Simulação começa do zero
          </div>
          <p class="text-xs leading-relaxed text-amber-900/80 dark:text-amber-100/80">
            As respostas que você já deu no <strong>Modo Estudo</strong> não
            entram na simulação. A prova começa em branco, com seu próprio
            cronômetro e regras. O progresso do estudo continua salvo
            separadamente — você não vai perder nada.
          </p>
        </div>

        {/* === ESCOPO === */}
        <Section title="Escopo" icon={FilterIcon}>
          <RadioCard
            checked={escopoTipo === "todas"}
            onSelect={() => setEscopoTipo("todas")}
            title="Todas as questões"
            desc={`Inclui todas as ${questoes.length} questões do banco.`}
          />
          <RadioCard
            checked={escopoTipo === "personalizar"}
            onSelect={() => setEscopoTipo("personalizar")}
            title="Personalizar"
            desc="Filtre por tema, fonte ou dificuldade."
          />
          {escopoTipo === "personalizar" && (
            <div class="ml-6 mt-2 space-y-3 rounded-md border border-border bg-background/40 p-3">
              {cat.temas.length > 0 && (
                <CatGroup
                  title="Temas"
                  items={cat.temas}
                  selected={temasSel}
                  onToggle={(k) => {
                    const next = new Set(temasSel);
                    if (next.has(k)) next.delete(k);
                    else next.add(k);
                    setTemasSel(next);
                  }}
                  onClear={() => setTemasSel(new Set())}
                />
              )}
              {cat.fontes.length > 0 && (
                <CatGroup
                  title="Fontes"
                  items={cat.fontes}
                  selected={fontesSel}
                  onToggle={(k) => {
                    const next = new Set(fontesSel);
                    if (next.has(k)) next.delete(k);
                    else next.add(k);
                    setFontesSel(next);
                  }}
                  onClear={() => setFontesSel(new Set())}
                />
              )}
              {cat.dificuldades.length > 0 && (
                <div>
                  <div class="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Dificuldade
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    {cat.dificuldades.map((d) => {
                      const sel = dificuldadesSel.has(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            const next = new Set(dificuldadesSel);
                            if (next.has(d)) next.delete(d);
                            else next.add(d);
                            setDificuldadesSel(next);
                          }}
                          class={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            sel
                              ? "border-amber-500/60 bg-amber-500/20 text-amber-700 dark:text-amber-300"
                              : "border-border bg-background hover:bg-accent"
                          }`}
                        >
                          {"★".repeat(d)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <RadioCard
            checked={escopoTipo === "numeros"}
            onSelect={() => setEscopoTipo("numeros")}
            title="Por números"
            desc="Ex.: 1, 3, 5-8, 12. Use vírgulas e intervalos."
          />
          {escopoTipo === "numeros" && (
            <input
              type="text"
              value={numerosRaw}
              onInput={(e) => setNumerosRaw((e.target as HTMLInputElement).value)}
              placeholder={`Ex.: 1, 3, 5-8 (até ${questoes.length})`}
              class="ml-6 mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          )}
        </Section>

        {/* === TEMPO === */}
        <Section title="Tempo" icon={Clock}>
          <div class="rounded-md border border-border bg-card/40 px-3 py-3">
            <div class="flex flex-wrap items-end gap-3">
              <NumberField
                label="Horas"
                value={horas}
                min={0}
                max={6}
                onChange={setHoras}
              />
              <NumberField
                label="Minutos"
                value={minutos}
                min={0}
                max={59}
                step={5}
                onChange={setMinutos}
              />
              <div class="ml-auto text-sm text-muted-foreground">
                {tempoAlvoMs === 0 ? (
                  <span class="italic">Sem limite (modo livre)</span>
                ) : (
                  <span>
                    Total:{" "}
                    <span class="font-semibold text-foreground">
                      {formatHMShort(tempoAlvoMs)}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <label class="mt-3 flex items-center gap-2 text-sm">
              <Switch
                checked={regressivo}
                onCheckedChange={(v) => setRegressivo(v)}
                disabled={tempoAlvoMs === 0}
              />
              <span>
                Cronômetro regressivo
                <span class="ml-1 text-xs text-muted-foreground">
                  (encerra quando chega em 0)
                </span>
              </span>
            </label>
          </div>
        </Section>

        {/* === EXIBIÇÃO === */}
        <Section title="Exibição" icon={Eye}>
          <ToggleRow
            label="Mostrar cronômetro geral"
            desc="Tempo total da simulação no topo da tela."
            checked={mostrarRelogioGeral}
            onChange={setMostrarRelogioGeral}
          />
          <ToggleRow
            label="Mostrar cronômetro da questão atual"
            desc="Tempo gasto na questão em que você está agora."
            checked={mostrarRelogioQuestao}
            onChange={setMostrarRelogioQuestao}
          />
          <ToggleRow
            label="Mostrar tempo por questão (restante ÷ não respondidas)"
            desc="Quanto sobra por questão se você dividir o tempo restante. Sem tempo máximo: aparece 'Não Definido'."
            checked={mostrarRelogioRitmo}
            onChange={setMostrarRelogioRitmo}
          />
          <ToggleRow
            label="Ocultar tema"
            desc="Não mostra a categoria/tema da questão (foco no enunciado)."
            checked={ocultarTema}
            onChange={setOcultarTema}
          />
          <ToggleRow
            label="Ocultar dificuldade"
            desc="Não mostra as estrelinhas de dificuldade."
            checked={ocultarDificuldade}
            onChange={setOcultarDificuldade}
          />
        </Section>

        {!podeIniciar && (
          <div class="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
            <AlertCircle className="size-4 shrink-0" />
            Nenhuma questão no escopo. Ajuste os filtros pra começar.
          </div>
        )}
      </div>

      <footer class="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-card/50 px-6 py-3">
        <div class="text-sm">
          <span class="text-muted-foreground">Escopo: </span>
          <span class="font-semibold text-foreground">
            {escopoCount} {escopoCount === 1 ? "questão" : "questões"}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={requestClose}
            class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!podeIniciar}
            class="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Flag width="1.3em" height="1.3em" />
            Iniciar simulação
          </button>
        </div>
      </footer>
    </>
  );
}

function Section({
  title,
  icon: Icon,
  children
}: {
  title: string;
  icon: typeof FilterIcon;
  children: any;
}) {
  return (
    <section class="space-y-2">
      <h3 class="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <Icon width="1.3em" height="1.3em" />
        {title}
      </h3>
      <div class="space-y-2">{children}</div>
    </section>
  );
}

function RadioCard({
  checked,
  onSelect,
  title,
  desc
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      class={`flex w-full items-start gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
        checked
          ? "border-primary bg-primary/5 ring-1 ring-primary/30"
          : "border-border bg-card/40 hover:bg-accent/30"
      }`}
    >
      <span
        class={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border-2 ${
          checked ? "border-primary" : "border-border"
        }`}
      >
        {checked && <span class="size-2 rounded-full bg-primary" />}
      </span>
      <div class="flex-1">
        <div class="text-sm font-semibold">{title}</div>
        <div class="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

function CatGroup({
  title,
  items,
  selected,
  onToggle,
  onClear
}: {
  title: string;
  items: Array<[string, number]>;
  selected: Set<string>;
  onToggle: (key: string) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <div class="mb-1.5 flex items-center justify-between">
        <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={onClear}
            class="text-[11px] text-primary hover:underline"
          >
            Limpar
          </button>
        )}
      </div>
      <div class="flex flex-wrap gap-1.5">
        {items.map(([k, n]) => {
          const sel = selected.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => onToggle(k)}
              class={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                sel
                  ? "border-primary/60 bg-primary/15 text-primary"
                  : "border-border bg-background hover:bg-accent"
              }`}
            >
              {k}
              <span class="ml-1 opacity-50">({n})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  desc,
  checked,
  onChange
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label class="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2.5 transition-colors hover:bg-accent/30">
      <div class="min-w-0 flex-1">
        <div class="text-sm font-medium">{label}</div>
        <div class="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={(v) => onChange(v)}
        className="shrink-0"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label class="flex flex-col gap-1">
      <span class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        min={min}
        max={max}
        step={step ?? 1}
        value={value}
        onInput={(e) => {
          const v = Number((e.target as HTMLInputElement).value);
          if (Number.isFinite(v)) {
            onChange(Math.max(min, Math.min(max, v)));
          }
        }}
        class="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-center text-sm font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </label>
  );
}

function formatHMShort(ms: number): string {
  const total = Math.floor(ms / 60_000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ""}`;
  return `${m}m`;
}
